/**
 * HeavyMach — Shared UI Components
 * Clean, professional B2B design system
 */
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';


export const C = {
  bg: '#f1f5f9',
  card: '#ffffff',
  blue: '#334155',
  blueDk: '#1e293b',
  blueLt: '#f8fafc',
  orange: '#334155',
  orangeLt: '#f8fafc',
  green: '#15803d',
  greenLt: '#f0fdf4',
  red: '#dc2626',
  redLt: '#fef2f2',
  amber: '#b45309',
  amberLt: '#fffbeb',
  attn: '#ea580c',
  attnLt: '#fff7ed',
  ink: '#0f172a',
  muted: '#475569',
  faint: '#94a3b8',
  border: '#e2e8f0',
  borderDk: '#cbd5e1',
};
export const F = "'Inter',-apple-system,BlinkMacSystemFont,sans-serif";

/* ── Genec header icon button ─────────────────────────────────
   ONE style for every icon that lives in a genec-system header (feed search /
   expand, workspace collapse, panel chevrons + close, chat/support close):
   a borderless, softly-rounded square with a light tint of the icon's ACTION
   colour; on hover the button FILLS solid with that colour (glyph → white).
   Tones (colour = what the action means, not one colour for everything):
     (default) slate — neutral: search, chevron menus, back, filter
     .indigo         — workspace expand / collapse
     .red            — destructive-ish: close ✕
   Include ${'${ICON_BTN_CSS}'} inside the component's <style> tag and use
   className="hm-iconbtn [tone]" on the <button>; add "active" to hold the
   filled state (don't fight it with inline colours). */
export const ICON_BTN_CSS = `
  .hm-iconbtn {
    --ic: ${C.muted}; --icbg: ${C.bg};
    width: 26px; height: 26px; padding: 0; flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    background: var(--icbg); border: none; border-radius: 6px;
    color: var(--ic); cursor: pointer;
    transition: background .15s, color .15s;
  }
  .hm-iconbtn.indigo { --ic: #6366f1; --icbg: #eef2ff; }
  .hm-iconbtn.blue   { --ic: #2563eb; --icbg: #eff6ff; }
  .hm-iconbtn.red    { --ic: ${C.red}; --icbg: ${C.redLt}; }
  .hm-iconbtn:hover, .hm-iconbtn:active, .hm-iconbtn.active {
    background: var(--ic); color: #fff;
  }
`;

// ── Font Loader ───────────────────────────────────
export function useFont() {
  useEffect(() => {
    if (document.getElementById('hm-inter')) return;
    const l = document.createElement('link');
    l.id = 'hm-inter';
    l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap';
    document.head.appendChild(l);
    document.body.style.fontFamily = F;
    document.body.style.background = C.bg;
    document.body.style.color = C.ink;
  }, []);
}

// ── Button ────────────────────────────────────────
export function Btn({ children, onClick, variant = 'primary', size = 'md', disabled, full, type = 'button', style = {}, className = '' }) {
  const [hov, setHov] = useState(false);
  const v = {
    primary: { bg: hov ? C.blueDk : C.blue, color: '#fff', border: 'none', shadow: hov ? '0 4px 12px rgba(51,65,85,.4)' : '0 1px 3px rgba(51,65,85,.3)' },
    secondary: { bg: hov ? '#f8fafc' : C.card, color: C.blue, border: `1.5px solid ${C.blue}`, shadow: 'none' },
    danger: { bg: hov ? '#b91c1c' : C.red, color: '#fff', border: 'none', shadow: 'none' },
    success: { bg: hov ? '#14532d' : C.green, color: '#fff', border: 'none', shadow: 'none' },
    ghost: { bg: hov ? C.bg : 'transparent', color: C.muted, border: `1.5px solid ${C.border}`, shadow: 'none' },
    orange: { bg: hov ? C.blueDk : C.orange, color: '#fff', border: 'none', shadow: hov ? '0 4px 12px rgba(51,65,85,.4)' : '0 1px 3px rgba(51,65,85,.3)' },
  }[variant] || {};
  const s = { xs: { padding: '4px 10px', fontSize: 11 }, sm: { padding: '6px 14px', fontSize: 12 }, md: { padding: '9px 20px', fontSize: 13 }, lg: { padding: '12px 28px', fontSize: 15 } }[size] || {};
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={className}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: v.bg, color: v.color, border: v.border || 'none',
        boxShadow: v.shadow, ...s,
        fontFamily: F, fontWeight: 600, borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        width: full ? '100%' : 'auto',
        display: 'inline-flex', alignItems: 'center', gap: 7,
        transition: 'all .15s', whiteSpace: 'nowrap',
        ...style,
      }}>
      {children}
    </button>
  );
}

// ── Input ─────────────────────────────────────────
export function Input({ label, required, error, hint, ...props }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      {label && (
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 5 }}>
          {label} {required && <span style={{ color: C.red }}>*</span>}
        </label>
      )}
      <input
        {...props}
        onFocus={e => { setFocus(true); props.onFocus?.(e); }}
        onBlur={e => { setFocus(false); props.onBlur?.(e); }}
        style={{
          width: '100%', padding: '10px 13px',
          border: `1.5px solid ${error ? C.red : focus ? C.blue : C.border}`,
          borderRadius: 8, fontSize: 14, fontFamily: F, color: C.ink,
          background: props.disabled ? C.bg : C.card,
          outline: 'none', boxSizing: 'border-box',
          boxShadow: focus ? `0 0 0 3px ${C.blueLt}` : 'none',
          transition: 'border-color .15s, box-shadow .15s',
          ...props.style,
        }}
      />
      {error && <p style={{ color: C.red, fontSize: 12, marginTop: 4, fontFamily: F }}>{error}</p>}
      {hint && !error && <p style={{ color: C.faint, fontSize: 12, marginTop: 4, fontFamily: F }}>{hint}</p>}
    </div>
  );
}

// ── Textarea ──────────────────────────────────────
export function Textarea({ label, required, error, rows = 3, bg, ...props }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 5 }}>{label} {required && <span style={{ color: C.red }}>*</span>}</label>}
      <textarea
        {...props} rows={rows}
        onFocus={e => { setFocus(true); props.onFocus?.(e); }}
        onBlur={e => { setFocus(false); props.onBlur?.(e); }}
        style={{
          width: '100%', padding: '10px 13px',
          border: `1.5px solid ${error ? C.red : focus ? C.blue : C.border}`,
          borderRadius: 8, fontSize: 14, fontFamily: F, color: C.ink,
          background: bg || C.card, outline: 'none', boxSizing: 'border-box', resize: 'vertical',
          boxShadow: focus ? `0 0 0 3px ${C.blueLt}` : 'none',
          transition: 'border-color .15s, box-shadow .15s',
        }}
      />
      {error && <p style={{ color: C.red, fontSize: 12, marginTop: 4 }}>{error}</p>}
    </div>
  );
}

// ── Select ────────────────────────────────────────
export function Select({ label, required, error, options = [], ...props }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 5 }}>{label} {required && <span style={{ color: C.red }}>*</span>}</label>}
      <select
        {...props}
        onFocus={e => { setFocus(true); props.onFocus?.(e); }}
        onBlur={e => { setFocus(false); props.onBlur?.(e); }}
        style={{
          width: '100%', padding: '10px 13px',
          border: `1.5px solid ${error ? C.red : focus ? C.blue : C.border}`,
          borderRadius: 8, fontSize: 14, fontFamily: F, color: props.value ? C.ink : C.faint,
          background: C.card, outline: 'none', boxSizing: 'border-box',
          boxShadow: focus ? `0 0 0 3px ${C.blueLt}` : 'none',
          cursor: 'pointer',
          ...props.style,
        }}>
        <option value="">{props.placeholder || 'Select...'}</option>
        {options.map(o => typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {error && <p style={{ color: C.red, fontSize: 12, marginTop: 4 }}>{error}</p>}
    </div>
  );
}

// ── SearchableSelect ──────────────────────────────
export function SearchSelect({ label, required, options = [], value, onChange, placeholder = 'Search...', disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  const inp = useRef(null);
  const filtered = q ? options.filter(o => o.toLowerCase().includes(q.toLowerCase())) : options;

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => { if (open) setTimeout(() => inp.current?.focus(), 40); }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', marginBottom: 16, fontFamily: F }}>
      {label && <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 5 }}>{label} {required && <span style={{ color: C.red }}>*</span>}</label>}
      <div onClick={() => !disabled && setOpen(v => !v)}
        style={{
          padding: '10px 13px', border: `1.5px solid ${open ? C.blue : C.border}`,
          borderRadius: 8, fontSize: 14, cursor: disabled ? 'not-allowed' : 'pointer',
          background: disabled ? C.bg : C.card, color: value ? C.ink : C.faint,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          boxShadow: open ? `0 0 0 3px ${C.blueLt}` : 'none',
          userSelect: 'none', transition: 'all .15s',
        }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{value || placeholder}</span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {value && !disabled && (
            <span onClick={e => { e.stopPropagation(); onChange(''); }} style={{ color: C.faint, cursor: 'pointer', lineHeight: 1, padding: '0 2px' }}>✕</span>
          )}
          <span style={{ color: C.faint, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s', lineHeight: 1 }}>▾</span>
        </div>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: C.card, border: `1.5px solid ${C.border}`, borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,.12)', zIndex: 9999, overflow: 'hidden',
        }}>
          <div style={{ padding: 8, borderBottom: `1px solid ${C.border}` }}>
            <input ref={inp} value={q} onChange={e => setQ(e.target.value)}
              onClick={e => e.stopPropagation()} placeholder="Search..."
              style={{ width: '100%', padding: '8px 10px', border: `1.5px solid ${C.border}`, borderRadius: 6, fontSize: 13, fontFamily: F, outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.target.style.borderColor = C.blue}
              onBlur={e => e.target.style.borderColor = C.border} />
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 220 }}>
            {filtered.length === 0
              ? <div style={{ padding: '16px 12px', color: C.faint, fontSize: 13, textAlign: 'center' }}>No results</div>
              : filtered.slice(0, 150).map(opt => (
                <div key={opt} onClick={() => { onChange(opt); setOpen(false); setQ(''); }}
                  style={{
                    padding: '9px 14px', fontSize: 14, cursor: 'pointer',
                    background: opt === value ? C.blueLt : 'transparent',
                    color: opt === value ? C.blue : C.ink, fontWeight: opt === value ? 600 : 400,
                  }}
                  onMouseEnter={e => { if (opt !== value) e.currentTarget.style.background = C.bg; }}
                  onMouseLeave={e => { if (opt !== value) e.currentTarget.style.background = 'transparent'; }}>
                  {opt} {opt === value && '✓'}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Card ──────────────────────────────────────────
export function Card({ children, style = {}, p = 24 }) {
  return (
    <div style={{
      background: C.card, borderRadius: 12,
      border: `1px solid ${C.border}`,
      boxShadow: '0 1px 3px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04)',
      padding: p,
      ...style,
    }}>{children}</div>
  );
}

// ── Badge / Status ────────────────────────────────
const STATUS_COLORS = {
  open: { bg: '#f1f5f9', color: '#334155', label: 'Open' },
  quoted: { bg: '#f5f3ff', color: '#6d28d9', label: 'Quoted' },
  confirmed: { bg: '#f0fdf4', color: '#15803d', label: 'Confirmed' },
  completed: { bg: '#f0fdf4', color: '#14532d', label: 'Completed' },
  cancelled: { bg: '#fef2f2', color: '#991b1b', label: 'Cancelled' },
  pending: { bg: '#fffbeb', color: '#92400e', label: 'Pending' },
  accepted: { bg: '#f0fdf4', color: '#15803d', label: 'Accepted' },
  rejected: { bg: '#fef2f2', color: '#991b1b', label: 'Rejected' },
  sent_to_supplier: { bg: '#fff7ed', color: '#c2410c', label: 'Sent to Supplier' },
  changes_requested: { bg: '#fffbeb', color: '#92400e', label: 'Changes Requested' },
  signed_by_supplier: { bg: '#f5f3ff', color: '#6d28d9', label: 'Signed by Supplier' },
  fully_signed: { bg: '#f0fdf4', color: '#15803d', label: 'Fully Signed' },
  advance_paid: { bg: '#f0fdf4', color: '#15803d', label: 'Advance Paid' },
  active: { bg: '#f0fdf4', color: '#15803d', label: 'Active' },
};

export function Badge({ status, tone, children, pulse = false, onClick }) {
  const s = STATUS_COLORS[status] || {
    bg: tone === 'green' ? '#f0fdf4' : tone === 'red' ? '#fef2f2' : tone === 'blue' ? '#f1f5f9' : tone === 'amber' ? '#fffbeb' : '#f1f5f9',
    color: tone === 'green' ? '#15803d' : tone === 'red' ? '#991b1b' : tone === 'blue' ? '#334155' : tone === 'amber' ? '#92400e' : C.muted,
  };
  const clickable = typeof onClick === 'function';
  // Don't let a clickable badge trigger the row's own click/expand handler.
  const handleClick = clickable ? (e) => { e.stopPropagation(); onClick(e); } : undefined;
  return (
    <span
      onClick={handleClick}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(e); } } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? 'Click to open & take action' : undefined}
      style={{
        background: s.bg, color: s.color,
        padding: '3px 10px', borderRadius: 99,
        fontSize: 11, fontWeight: 600, fontFamily: F,
        whiteSpace: 'nowrap', display: 'inline-block',
        ...(clickable ? { cursor: 'pointer' } : null),
        ...(pulse ? { animation: 'badgePulse 1.6s ease-in-out infinite', transformOrigin: 'center' } : null),
      }}>
      {pulse && <style>{`@keyframes badgePulse{0%,100%{transform:scale(1)}50%{transform:scale(1.12)}}`}</style>}
      {children || s.label || status?.replace(/_/g, ' ')}
    </span>
  );
}

// Split-pill: [Label | Value] — left side muted, right side colored status
export function LabelBadge({ label, status, tone, children, pulse = false, onClick }) {
  const s = STATUS_COLORS[status] || {
    bg: tone === 'green' ? '#f0fdf4' : tone === 'red' ? '#fef2f2' : tone === 'blue' ? '#dbeafe' : tone === 'amber' ? '#fffbeb' : '#f1f5f9',
    color: tone === 'green' ? '#15803d' : tone === 'red' ? '#991b1b' : tone === 'blue' ? '#1e40af' : tone === 'amber' ? '#92400e' : C.muted,
    label: children,
  };
  const clickable = typeof onClick === 'function';
  const handleClick = clickable ? (e) => { e.stopPropagation(); onClick(e); } : undefined;
  return (
    <span
      onClick={handleClick}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(e); } } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? 'Click to open & take action' : undefined}
      style={{
        display: 'inline-flex', alignItems: 'center', borderRadius: 99,
        overflow: 'hidden', border: `1px solid ${C.border}`,
        fontFamily: F, fontWeight: 600, whiteSpace: 'nowrap',
        ...(pulse ? { animation: 'badgePulse 1.6s ease-in-out infinite', transformOrigin: 'center' } : null),
        ...(clickable ? { cursor: 'pointer' } : null),
      }}>
      {pulse && <style>{`@keyframes badgePulse{0%,100%{transform:scale(1)}50%{transform:scale(1.12)}}`}</style>}
      <span style={{ background: '#f1f5f9', color: C.muted, padding: '3px 8px', fontSize: 10 }}>{label}</span>
      <span style={{ background: s.bg, color: s.color, padding: '3px 9px', fontSize: 11 }}>{children || s.label || status?.replace(/_/g, ' ')}</span>
    </span>
  );
}

// ── Spinner ───────────────────────────────────────
export function Spinner({ size = 28, color = C.blue }) {
  return (
    <>
      <div style={{ width: size, height: size, border: `3px solid ${C.border}`, borderTopColor: color, borderRadius: '50%', animation: 'spin .7s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </>
  );
}

export function PageLoad() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <Spinner size={36} />
    </div>
  );
}

// ── Empty State ───────────────────────────────────
export function Empty({ title, desc, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '52px 24px' }}>
      <div style={{ width: 52, height: 52, borderRadius: 14, background: C.bg, border: `1.5px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <rect x="2" y="5" width="18" height="14" rx="2" stroke={C.faint} strokeWidth="1.5"/>
          <path d="M6 5V4a2 2 0 012-2h6a2 2 0 012 2v1" stroke={C.faint} strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M7 11h8M7 15h5" stroke={C.faint} strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, marginBottom: 6, fontFamily: F }}>{title}</div>
      {desc && <p style={{ color: C.muted, fontSize: 13, marginBottom: 0, maxWidth: 340, margin: '0 auto', lineHeight: 1.6, fontFamily: F }}>{desc}</p>}
      {action && <div style={{ marginTop: 20 }}>{action}</div>}
    </div>
  );
}

/* ── FramedPanel — the SplitDetail-style box for list pages.
   Viewport-fitted so content scrolls inside it like the side nav. ── */
export function FramedPanel({ accent = C.orange, children, style = {} }) {
  const ref = useRef(null);
  const [fitH, setFitH] = useState(null);
  useLayoutEffect(() => {
    const calc = () => {
      const el = ref.current;
      if (el) setFitH(Math.max(360, window.innerHeight - el.getBoundingClientRect().top - 4));
    };
    calc();
    const t = setTimeout(calc, 250);
    window.addEventListener('resize', calc);
    return () => { clearTimeout(t); window.removeEventListener('resize', calc); };
  }, []);
  return (
    <div ref={ref} className="hm-panelin"
      style={{
        border: `1px solid ${C.border}`,
        borderTop: `3px solid ${accent}`,
        borderRadius: 12,
        background: C.card,
        padding: 18,
        height: fitH || 460,
        marginBottom: -28,
        overflowY: 'auto',
        boxShadow: `0 10px 30px ${accent}14`,
        // No entrance animation: the panel is reused across kept-alive tabs
        // (display none↔block), and any animation would replay on every switch,
        // causing a blink. Instant show keeps tab switching smooth + stable.
        animation: 'none',
        ...style,
      }}>
      {children}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────
// Rendered via createPortal so the fixed-position overlay always escapes any
// ancestor with `transform` / `filter` / `perspective` (which would otherwise
// create a containing block and trap `position: fixed` inside it, leaving the
// modal half-covering the page).
export function Modal({ open, onClose, title, children, width = 520, sidebarOffset = 0 }) {
  if (!open) return null;
  if (typeof document === 'undefined') return null;
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const offset = isMobile ? 0 : sidebarOffset;
  return createPortal(
    <div onClick={onClose} style={{
      position: 'fixed', top: 0, bottom: 0, left: offset, right: 0,
      background: 'rgba(15,23,42,.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16, backdropFilter: 'blur(3px)',
      animation: 'hmModalBg .18s ease both',
    }}>
      <style>{`
        @keyframes hmModalBg  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes hmModalBox { from { opacity: 0; transform: scale(.94) translateY(12px); } to { opacity: 1; transform: scale(1) translateY(0); } }
      `}</style>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.card, borderRadius: isMobile ? 12 : 16, width: '100%', maxWidth: width,
        maxHeight: isMobile ? '95vh' : '90vh', overflow: 'auto',
        boxShadow: '0 24px 64px rgba(0,0,0,.22), 0 4px 16px rgba(0,0,0,.10)',
        animation: 'hmModalBox .22s cubic-bezier(.34,1.28,.64,1) both',
      }}>
        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.ink, fontFamily: F }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: C.faint, lineHeight: 1, padding: '0 4px', borderRadius: 6, transition: 'background .15s', }}
            onMouseEnter={e => e.currentTarget.style.background = C.bg}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>×</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}

// ── Stat Card ─────────────────────────────────────
export function StatCard({ label, value, icon, tone = 'blue', sub }) {
  const tones = {
    blue: { bg: '#f1f5f9', ic: '#334155' },
    green: { bg: '#f0fdf4', ic: '#15803d' },
    orange: { bg: '#f1f5f9', ic: '#334155' },
    amber: { bg: '#fffbeb', ic: '#b45309' },
    red: { bg: '#fef2f2', ic: '#dc2626' },
    purple: { bg: '#f5f3ff', ic: '#7c3aed' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <Card style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 20 }}>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: t.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: t.ic }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 26, fontWeight: 800, color: C.ink, lineHeight: 1, letterSpacing: '-.02em' }}>{value}</div>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 3 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: C.faint, marginTop: 2 }}>{sub}</div>}
      </div>
    </Card>
  );
}

// ── Table ─────────────────────────────────────────
export function Table({ cols, rows, empty = 'No data', minWidth = 640 }) {
  if (!rows?.length) return <div style={{ padding: '40px 24px', textAlign: 'center', color: C.faint, fontSize: 14 }}>{empty}</div>;
  return (
    <div className="hm-scroll-x">
      {/* minWidth keeps columns at full size and scrolls sideways on mobile
          instead of crushing them (see .hm-scroll-x convention in index.js) */}
      <table style={{ width: '100%', minWidth, borderCollapse: 'collapse', fontFamily: F }}>
        <thead>
          <tr style={{ background: C.bg }}>
            {cols.map(c => (
              <th key={c.key} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '.06em', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              {cols.map(c => (
                <td key={c.key} style={{ padding: '14px 16px', fontSize: 13, color: C.ink, verticalAlign: 'middle' }}>
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Tabs ──────────────────────────────────────────
export function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${C.border}`, marginBottom: 24 }}>
      {tabs.map(t => (
        <button key={t.key} onClick={() => onChange(t.key)}
          style={{
            padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 14, fontWeight: active === t.key ? 600 : 500, fontFamily: F,
            color: active === t.key ? C.blue : C.muted,
            borderBottom: `2px solid ${active === t.key ? C.blue : 'transparent'}`,
            marginBottom: -1, transition: 'all .15s',
          }}>
          {t.label} {t.count != null && <span style={{ marginLeft: 5, background: active === t.key ? C.blueLt : C.bg, color: active === t.key ? C.blue : C.faint, borderRadius: 99, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

// ── Page Header ───────────────────────────────────
export function PageHeader({ title, subtitle, action, actionAlign = 'center' }) {
  if (actionAlign === 'title') {
    return (
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: C.ink, letterSpacing: '-.02em', fontFamily: F }}>{title}</h1>
          {action && <div style={{ flexShrink: 0 }}>{action}</div>}
        </div>
        {subtitle && <p style={{ margin: '4px 0 0', fontSize: 14, color: C.muted }}>{subtitle}</p>}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: actionAlign === 'bottom' ? 'flex-end' : 'center', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: C.ink, letterSpacing: '-.02em', fontFamily: F }}>{title}</h1>
        {subtitle && <p style={{ margin: '4px 0 0', fontSize: 14, color: C.muted }}>{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

// ── HeaderTabs — pill toggle, sits inline in PageHeader action ──
export function HeaderTabs({ tabs, active, onChange, extra, animated }) {
  const pills = (
    <div style={{
      display: 'flex', gap: 4, padding: 4, background: C.bg,
      border: animated ? 'none' : `1px solid ${C.border}`, borderRadius: 10,
    }}>
      {tabs.map(t => {
          const on = active === t.key;
          const ac = t.accent || C.blue;
          return (
            <button key={t.key} onClick={() => onChange(t.key)} className={`hm-seg${on ? ' is-active' : ''}`}
              style={{
                padding: '7px 14px', borderRadius: 7, border: 'none', cursor: 'pointer',
                background: on ? ac : 'transparent',
                color: on ? '#fff' : C.muted,
                fontFamily: F, fontSize: 13, fontWeight: on ? 700 : 600,
                display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap',
                boxShadow: on ? `0 4px 12px ${ac}40` : 'none',
              }}>
              <span style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 7 }}>
                {t.label}
                {t.count > 0 && (
                  <span style={{
                    background: C.attn,
                    color: '#fff',
                    fontSize: 11, padding: '1px 7px', borderRadius: 99, fontWeight: 700,
                    boxShadow: on ? '0 0 0 1.5px rgba(255,255,255,.6)' : 'none',
                  }}>{t.count}</span>
                )}
              </span>
            </button>
          );
      })}
    </div>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'nowrap' }}>
      {animated ? <div className="hm-tabring">{pills}</div> : pills}
      {extra}
    </div>
  );
}

// ── Alert ─────────────────────────────────────────
export function Alert({ tone = 'blue', children }) {
  const tones = {
    blue: { bg: C.blueLt, color: '#1e293b', border: '#cbd5e1' },
    green: { bg: C.greenLt, color: '#14532d', border: '#a7f3d0' },
    amber: { bg: C.amberLt, color: '#78350f', border: '#fde68a' },
    red: { bg: C.redLt, color: '#7f1d1d', border: '#fecaca' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <div style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 8, padding: '12px 16px', color: t.color, fontSize: 13, fontFamily: F, lineHeight: 1.6, marginBottom: 16 }}>
      {children}
    </div>
  );
}

// ── Chat icon (WhatsApp green) ────────────────────
export function ChatIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill="#128C7E" stroke="#128C7E" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="8" y1="9" x2="16" y2="9" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
      <line x1="8" y1="13" x2="14" y2="13" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  );
}

// ── Print icon (SVG) ─────────────────────────────
export function PrintIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0, marginRight: 5 }}>
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  );
}

// ── Trash icon (SVG) ──────────────────────────────
export function TrashIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', flexShrink: 0 }}>
      <path d="M2 4h12M5 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1M6 7v5M10 7v5M3 4l.9 9a1 1 0 0 0 1 .9h6.2a1 1 0 0 0 1-.9L13 4" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ── Money formatter ───────────────────────────────
export function money(n) {
  return '₹' + Number(n || 0).toLocaleString('en-IN');
}

// Backend stores timestamps as UTC but returns naive strings (no timezone marker).
// JS `new Date("2026-05-25 07:45:00")` would interpret that as local time, showing
// IST users a time 5h30m behind reality. Force-tag naive timestamps as UTC so the
// browser converts to the viewer's local zone correctly.
function parseBackendDate(d) {
  if (d instanceof Date) return d;
  if (typeof d !== 'string') return new Date(d);
  // Already has a timezone marker (Z, +HH:MM, -HH:MM at the end) — trust it.
  if (/(Z|[+-]\d{2}:?\d{2})$/i.test(d.trim())) return new Date(d);
  // Naive "YYYY-MM-DD HH:MM:SS" or ISO without zone — tag as UTC.
  const iso = d.replace(' ', 'T');
  return new Date(iso + 'Z');
}

// ── Date formatter ────────────────────────────────
// All formatters force timeZone: 'Asia/Kolkata' because the entire app is
// India-only (₹ pricing, Indian states/cities, IST business hours). Without
// this, a user on a laptop set to a non-IST timezone (or a browser that
// defaults to UTC for `toLocaleString`) would see times shifted by hours.
const IST = 'Asia/Kolkata';

export function fmtDate(d) {
  if (!d) return '—';
  return parseBackendDate(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: IST });
}

export function fmtTime(d) {
  if (!d) return '';
  const dt = parseBackendDate(d);
  const nowIst = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: IST });
  const dtIst  = dt.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: IST });
  if (dtIst === nowIst) return dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: IST });
  return dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: IST });
}

/* Always shows date AND time, e.g. "12 May 2026, 03:45 PM" — use whenever a record's exact moment matters (sent/received/created). */
export function fmtDateTime(d) {
  if (!d) return '—';
  const dt = parseBackendDate(d);
  return dt.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: IST });
}

// ── ProtectedRoute ────────────────────────────────
export function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoad />;
  if (!user) return <Navigate to={`/login?role=${roles?.[0] || 'customer'}`} />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" />;
  return children;
}
